import {
	CatalogManager,
	PhotoFlagStatus,
	PhotoMetadataRecord,
	PhotoRecord,
	Rating,
} from "@/lib/catalog"
import { createVirtualPhotoRecord } from "@/lib/catalog/utils"
import { filesAtom, getFileForPhoto } from "../files-state"
import {
	autoSelectNextPhotoWithRemoval,
	currentPhotoAtom,
	deletePhotoMetadata,
	displayedPhotosAtom,
	filePathsAtom,
	getCullingInfo,
	getPhotoMetadata,
	metadataAtom,
	photosAtom,
	selectedPhotosAtom,
	selectedPhotosIDsAtom,
	updateCullingInfo,
	updatePhoto,
	updatePhotoMetadata,
	updatePhotos,
	updatePhotosMetadata,
} from "./photos-state"
import { queueApplyStyle } from "../processing/image-processing"
import { store } from "../store"
import { currentStyleAtom } from "../style/style-state"
import { PhotoId } from "../undoable-state"
import { ColorTag } from "@/utils/exif"
import { saveCurrentAppState } from "../store-history"
import { MainMaskCache } from "@/storage/mask-cache"
import { showToast } from "../ui-views-state"
import { photosCustomOrderAtom } from "./photos-custom-order"
import { replaceDialog } from "../dialogs-state"
import { ConfirmDialog } from "@/components/Dialogs/ConfirmDialog"
import { Sentry } from "@/telemetry/sentry"
import { getProductName } from "@/utils/product-name"
import toast from "react-hot-toast"
import {
	Clusters,
	ensureCullingClusterLeaderNotInvalid,
	ensurePhotoNotKeyNeighbor,
	makeCullingClusterLeaderIfNeeded,
	makePhotoKeyNeighborIfNeeded,
} from "@/lib/ai/ai-cluster"
import { processPhotoIfNeeded } from "../style/apply-style"
import { analytics } from "@/telemetry/amplitude"
import { isCullingModeAtom, projectAtom, visibilityFiltersAtom } from "../project/projects-state"
import { autoAdvanceAfterTaggingAtom } from "../importing/culling-state"
import { nextPhoto } from "./photos-selection-state"
import { photosViewAtom } from "./photos-view-state"
import { loadCurrentFile } from "../editor/photo-loading"
import { isPhotoFullscreenAtom } from "../ui-views-state"
import {
	getOrExtractImageInfoWithLock,
	imageInfosAtom,
	updateImageInfo,
} from "../processing/image-info"
import { processingQueue } from "../processing/processing-queue"
import { getXmpMetadata, updateXmpFile } from "@/utils/xmp"
import { PreviewSyncService } from "../previews/previews-sync"
import { getApplyAIStyleProperties } from "../style/style-event-utils"
// eslint-disable-next-line no-restricted-imports -- Phase 1: store-layer toast i18n, to be refactored to UI-layer translation
import i18n from "@/i18n"
// eslint-disable-next-line no-restricted-imports
import { colorTagKeys } from "@/i18n/dual-role-strings"

export const autoAdvanceAfterTaggingIfNeeded = (actedPhotoId?: PhotoId) => {
	if (!store.get(isCullingModeAtom) || !store.get(autoAdvanceAfterTaggingAtom)) {
		return
	}

	const selectedIds = store.get(selectedPhotosIDsAtom)
	if (selectedIds.size !== 1) {
		return
	}

	const currentPhoto = store.get(currentPhotoAtom)
	if (!currentPhoto) {
		return
	}

	const selectedId = [...selectedIds][0]
	if (selectedId !== currentPhoto.id) {
		return
	}

	if (actedPhotoId && selectedId !== actedPhotoId) {
		return
	}

	const previousPhotoId = currentPhoto.id
	nextPhoto()

	const nextCurrentPhoto = store.get(currentPhotoAtom)
	const shouldLoadCurrentFile =
		store.get(photosViewAtom) !== "grid" || store.get(isPhotoFullscreenAtom)
	if (nextCurrentPhoto?.id !== previousPhotoId && shouldLoadCurrentFile) {
		void loadCurrentFile({ kinds: ["preview", "full"] })
	}
}

export const createVirtualCopy = async (actionedPhoto: PhotoRecord) => {
	const photos = store.get(photosAtom)
	const selectedPhotos = store.get(selectedPhotosAtom)
	const selectedPhotosIds = store.get(selectedPhotosIDsAtom)
	const metadata = store.get(metadataAtom)
	const files = store.get(filesAtom)
	const style = store.get(currentStyleAtom)
	const order = store.get(photosCustomOrderAtom)

	const sourcePhotos = selectedPhotosIds.has(actionedPhoto.id) ? selectedPhotos : [actionedPhoto]

	const addedPhotos: Record<PhotoId, PhotoRecord> = {}
	const addedFiles: Record<PhotoId, File> = {}
	const addedPhotoOrder: Record<PhotoId, number> = {}

	for (const photo of sourcePhotos) {
		const descendantPhotos = await CatalogManager.fetchDescendantPhotoRecords(photo)
		const extIndex = photo.fileName.lastIndexOf(".")
		const filename = extIndex > 0 ? photo.fileName.substring(0, extIndex) : photo.fileName
		const fileExt = extIndex > 0 ? photo.fileName.substring(extIndex + 1) : ""

		const sortingIndex = order[photo.id]
		const virtualPhoto = createVirtualPhotoRecord(photo, sortingIndex)

		virtualPhoto.fileName =
			(filename ?? "file") +
			"_copy_" +
			(descendantPhotos.length + 1).toString() +
			"." +
			fileExt

		const existingMetadata = metadata[photo.id]

		if (existingMetadata) {
			existingMetadata.photoId = virtualPhoto.id
			updatePhotoMetadata(virtualPhoto.id, existingMetadata)
		}

		const file = getFileForPhoto(photo)

		// Duplicate the image info from the source photo
		const sourceImageInfo = await getOrExtractImageInfoWithLock(photo)
		const imageInfoFromAtom = store.get(imageInfosAtom)[photo.id]
		console.log(
			"Duplicating image info for virtual copy:",
			photo.id,
			sourceImageInfo,
			imageInfoFromAtom,
		)
		updateImageInfo({ ...sourceImageInfo, imageId: virtualPhoto.id })

		const cullingInfo = getCullingInfo(photo.id)
		if (cullingInfo) {
			await updateCullingInfo(virtualPhoto.id, { ...cullingInfo })
		}

		addedPhotos[virtualPhoto.id] = virtualPhoto

		if (file) {
			addedFiles[virtualPhoto.id] = file
		}

		if (sortingIndex) {
			addedPhotoOrder[virtualPhoto.id] = sortingIndex
		}
	}

	store.set(filesAtom, {
		...files,
		...addedFiles,
	})

	store.set(photosAtom, {
		...photos,
		...addedPhotos,
	})

	store.set(photosCustomOrderAtom, {
		...order,
		...addedPhotoOrder,
	})

	for (const photo of Object.values(addedPhotos)) {
		await CatalogManager.storePhotoRecord(photo)

		if (photo.parentId && photo.kind === "locked") {
			await CatalogManager.copyAdjustmentHistoryRecords(photo.parentId, photo.id)
		}
	}

	if (style) {
		for (const photo of Object.values(addedPhotos)) {
			if (photo.kind === "locked") {
				continue
			}

			queueApplyStyle(photo, style, "create_virtual_copy")
		}

		const values = {
			count: Object.keys(addedPhotos).length,
			styleId: style.styleId,
			styleName: style.name,
			projectId: store.get(projectAtom)?.id,
			reason: "new_virtual_copies",
			...getApplyAIStyleProperties(style, false),
		}

		analytics.trackEvent({
			label: "apply_ai_style",
			customProperties: values,
			type: "event",
		})
	}
}

export const deleteSelectedVirtualCopies = () => {
	const selected = [...store.get(selectedPhotosIDsAtom)]
	deleteVirtualCopies(selected)
}

export const deleteVirtualCopies = (ids: PhotoId[]) => {
	const currentDisplayPhotos = store.get(displayedPhotosAtom)
	Promise.allSettled(ids.map((id) => deleteVirtualCopy(id)))
		.then((results) => {
			const deleted = results
				.filter((r): r is PromiseFulfilledResult<string> => r.status === "fulfilled")
				.map((r) => r.value)

			if (deleted.length < ids.length) {
				const failed = ids.length - deleted.length
				toast.error(i18n.t("store:storePhotos.deleteVirtualCopyFailed", { count: failed }))
			}

			autoSelectNextPhotoWithRemoval(deleted, currentDisplayPhotos)
		})
		.catch((e) => {
			//Errors should be caught and logged in the catalog manager already, this is just a safety net for any unhandled errors
			Sentry.captureException(e)
		})
}

const deleteVirtualCopy = async (photoId: string) => {
	await deletePhotoRecord(photoId)
	await deletePhotoMetadata(photoId)

	return photoId
}

const hasReferencePhotos = (photos: PhotoRecord[]) =>
	photos.some((photo) => photo.kind === "reference")

const blockReferencePhotoAction = (photos: PhotoRecord[], message: string) => {
	if (!hasReferencePhotos(photos)) {
		return false
	}

	toast.error(message, { duration: 5000 })
	return true
}

export const removeSelectedPhotos = () => {
	//Remove virtual copies and
	const selected = store.get(selectedPhotosAtom)
	const virtualCopies = selected.filter((p) => p.parentId !== null).map((p) => p.id)
	const originals = selected.filter((p) => p.parentId === null).map((p) => p.id)

	if (
		blockReferencePhotoAction(
			selected,
			i18n.t("components:photoContextMenu.toastRemovePhotoCannotRemoveReferences"),
		)
	) {
		return
	}

	if (virtualCopies.length > 0) {
		deleteVirtualCopies(virtualCopies)
	}

	if (originals.length > 0) {
		replaceDialog(ConfirmDialog, {
			trackingViewName: "remove_photos_dialog",
			canClose: true,
			title: i18n.t("components:photoContextMenu.toastRemovePhotoTitle", {
				count: originals.length,
			}),
			message: i18n.t("components:photoContextMenu.toastRemovePhotoMessage", {
				count: originals.length,
				productName: getProductName(),
			}),
			primary: {
				title: i18n.t("components:photoContextMenu.toastRemovePhotoHideIn", {
					productName: getProductName(),
				}),
				handler: () => {
					hidePhotos(originals)
				},
			},
			secondary: {
				title: i18n.t("components:photoContextMenu.toastRemovePhotoDeleteFromDisk"),
				isDestructive: true,
				handler: () => {
					deleteOriginalPhotos(originals)
				},
			},
		})
	}
}

export const deleteOriginalPhotos = async (ids: PhotoId[]) => {
	const currentDisplayPhotos = store.get(displayedPhotosAtom)
	await hidePhotos(ids, false) // call hidePhotos to reassign key photos properly before deleting
	Promise.allSettled(ids.map((id) => deleteOriginalPhoto(id)))
		.then((results) => {
			const deleted = results
				.filter((r) => r.status === "fulfilled")
				.map((r) => (r as PromiseFulfilledResult<string>).value)

			if (deleted.length < ids.length) {
				const failed = ids.length - deleted.length
				toast.error(i18n.t("store:storePhotos.deletePhotoFailed", { count: failed }))
			}

			autoSelectNextPhotoWithRemoval(deleted, currentDisplayPhotos)
		})
		.catch((e) => {
			//Errors should be caught and logged in the catalog manager already, this is just a safety net for any unhandled errors
			Sentry.captureException(e)
		})
}

const deleteOriginalPhoto = async (photoId: string) => {
	const photos = store.get(photosAtom)
	const photo = photos[photoId]
	if (!photo) {
		return
	}

	if (photo.kind === "reference") {
		return
	}

	await CatalogManager.removePhotoFile(photo)

	const copies = Object.values(photos).filter((p) => p.parentId === photoId)
	if (copies.length > 0) {
		// call hidePhotos to reassign key photos properly before deleting
		await hidePhotos(
			copies.map((p) => p.id),
			false,
		)
	}
	for (const copy of copies) {
		await deleteVirtualCopy(copy.id)
	}

	await deletePhotoMetadata(photoId)
	await deletePhotoRecord(photoId)

	return photoId
}

export const deletePhotoRecord = async (photoId: string) => {
	const { ...photos } = store.get(photosAtom)
	const project = store.get(projectAtom)

	delete photos[photoId]

	store.set(photosAtom, {
		...photos,
	})

	await CatalogManager.deletePhotoRecord(photoId)

	if (project) {
		await MainMaskCache.deletePhoto(project.id, photoId)
	}
	if (
		project &&
		project.previewThumbnailPhotoIds?.[0] &&
		photoId === project.previewThumbnailPhotoIds?.[0]
	) {
		//Project cover photo is deleted, need to sync a new one
		await PreviewSyncService.syncProjectCoverIfNeeded(project, true)
	}
}

export const canHideSelectedPhotos = (photos: PhotoRecord[]) =>
	photos.length > 0 && !hasReferencePhotos(photos) && photos.some((photo) => !photo.isHidden)

export const hideSelectedPhotos = async () => {
	const selected = store.get(selectedPhotosAtom)
	if (
		blockReferencePhotoAction(
			selected,
			i18n.t("components:photoContextMenu.toastHidePhotoCannotHideReferences"),
		)
	) {
		return
	}

	if (!canHideSelectedPhotos(selected)) {
		return
	}

	await hidePhotos(selected.map((photo) => photo.id))
}

export const hidePhotos = async (ids: PhotoId[], shouldShowToast: boolean = true) => {
	const photos = store.get(photosAtom)
	const hiddablePhotos = ids.filter((id) => !photos[id]?.isHidden)
	const displayedPhotos = store.get(displayedPhotosAtom)
	await updatePhotos((changes) => {
		for (const id of hiddablePhotos) {
			changes[id] = { isHidden: true, status: "none", currentTask: null }
		}
	})
	//Remove photos from processing queue to prevent unnecessary processing of hidden photos
	for (const id of ids) {
		processingQueue.removeTask(id)
	}

	let clusters: Clusters | null = null
	for (const id of ids) {
		clusters = ensurePhotoNotKeyNeighbor(id, clusters)
	}
	let cullingClusters: Clusters | null = null
	for (const id of ids) {
		cullingClusters = ensureCullingClusterLeaderNotInvalid(id, cullingClusters)
	}

	if (shouldShowToast) {
		showToast(i18n.t("components:photoContextMenu.hidePhoto", { count: ids.length }))
	}

	if (!store.get(visibilityFiltersAtom).has("hidden")) {
		autoSelectNextPhotoWithRemoval(hiddablePhotos, displayedPhotos)
	}

	const project = store.get(projectAtom)
	if (
		project &&
		project.previewThumbnailPhotoIds?.[0] &&
		ids.includes(project.previewThumbnailPhotoIds?.[0])
	) {
		//Project cover photo is hidden, need to sync a new one
		await PreviewSyncService.syncProjectCoverIfNeeded(project, true)
	}
}

export const unhidePhotos = async (ids: PhotoId[]) => {
	const photos = store.get(photosAtom)
	const unhiddablePhotos = ids.filter((id) => photos[id]?.isHidden)

	await updatePhotos((changes) => {
		for (const id of unhiddablePhotos) {
			changes[id] = { isHidden: false }
		}
	})

	let clusters: Clusters | null = null
	for (const id of unhiddablePhotos) {
		clusters = makePhotoKeyNeighborIfNeeded(id, clusters)
	}
	let cullingClusters: Clusters | null = null
	for (const id of unhiddablePhotos) {
		cullingClusters = makeCullingClusterLeaderIfNeeded(id, cullingClusters)
	}

	// process photos after all changes are done
	for (const id of unhiddablePhotos) {
		const photo = photos[id]
		if (photo && photo.kind != "locked" && photo.kind != "reference") {
			void processPhotoIfNeeded({ ...photo, isHidden: false }, "unhide_photo")
		}
	}

	const project = store.get(projectAtom)
	if (project && !project.previewThumbnailPhotoIds?.[0]) {
		// All photos were hidden and now at least one is unhidden, need to sync a cover photo
		await PreviewSyncService.syncProjectCoverIfNeeded(project, true)
	}

	showToast(i18n.t("components:photoContextMenu.unhidePhoto", { count: ids.length }))
}
export const unhideSelectedPhotos = async () => {
	return unhidePhotos([...store.get(selectedPhotosIDsAtom)])
}

export const ratePhoto = (rating: Rating, photo: PhotoRecord) => {
	autoAdvanceAfterTaggingIfNeeded(photo.id)
	updatePhoto(photo.id, { rating })

	const message =
		rating === 0
			? i18n.t("components:photoContextMenu.toastStarRatingCleared")
			: i18n.t("components:photoContextMenu.toastRatedStars", { count: rating })
	saveCurrentAppState(message)
	showToast(message)
}

export const rateSelectedPhotos = (rating: Rating) => {
	const selectedPhotos = store.get(selectedPhotosAtom)

	if (!selectedPhotos || selectedPhotos.length == 0) {
		return
	}

	autoAdvanceAfterTaggingIfNeeded()

	updatePhotos((photos) => {
		for (const selectedPhoto of selectedPhotos) {
			photos[selectedPhoto.id] = { rating }
		}
	})

	const message =
		rating === 0
			? i18n.t("components:photoContextMenu.toastStarRatingCleared")
			: i18n.t("components:photoContextMenu.toastRatedStars", { count: rating })
	saveCurrentAppState(message)
	showToast(message)
}

export const setColorTagForPhoto = (photo: PhotoRecord, colorTag: ColorTag) => {
	autoAdvanceAfterTaggingIfNeeded(photo.id)
	updatePhoto(photo.id, { colorTag })
	const color = i18n.t(`components:${colorTagKeys[colorTag]}`)
	const message =
		colorTag === "No Color"
			? i18n.t("components:photoContextMenu.toastColorRatingCleared")
			: i18n.t("components:photoContextMenu.toastSetColorRating", { color })

	saveCurrentAppState(message)
	showToast(message)
}

export const setColorTag = (colorTag: ColorTag) => {
	const selectedPhotos = store.get(selectedPhotosAtom)

	if (!selectedPhotos || selectedPhotos.length == 0) {
		return
	}

	const shouldClearTag = selectedPhotos.every((p) => p.colorTag === colorTag)
	const updatedColorTag = shouldClearTag ? "No Color" : colorTag

	autoAdvanceAfterTaggingIfNeeded()

	updatePhotos((photos) => {
		for (const selectedPhoto of selectedPhotos) {
			photos[selectedPhoto.id] = { colorTag: updatedColorTag }
		}
	})

	const color = i18n.t(`components:${colorTagKeys[updatedColorTag]}`)
	const message =
		updatedColorTag === "No Color"
			? i18n.t("components:photoContextMenu.toastColorRatingCleared")
			: i18n.t("components:photoContextMenu.toastSetColorRating", { color })

	saveCurrentAppState(message)
	showToast(message)
}

export const adjustCaptureTime = (offset: number) => {
	if (offset === 0) {
		return
	}
	const selectedPhotos = store.get(selectedPhotosAtom)

	if (!selectedPhotos || selectedPhotos.length == 0) {
		return
	}

	updatePhotosMetadata((metadata) => {
		for (const selectedPhoto of selectedPhotos) {
			const existingMetadata = getPhotoMetadata(selectedPhoto.id)
			if (existingMetadata && existingMetadata.dateCreated) {
				metadata[selectedPhoto.id] = {
					dateCreated: existingMetadata.dateCreated + offset,
					dateModified: Date.now(),
				}
			}
		}
	})

	showToast(i18n.t("components:photoContextMenu.toastCaptureTimeAdjusted"))
}

export const setFlag = (ids: PhotoId[], flag: PhotoFlagStatus) => {
	autoAdvanceAfterTaggingIfNeeded(ids.length === 1 ? ids[0] : undefined)

	updatePhotos((changes) => {
		for (const id of ids) {
			changes[id] = { flag }
		}
	})

	let message = ""
	switch (flag) {
		case "flagged":
			message = i18n.t("components:photoContextMenu.toastFlaggedAsPick", {
				count: ids.length,
			})
			break
		case "rejected":
			message = i18n.t("components:photoContextMenu.toastFlaggedAsRejected", {
				count: ids.length,
			})
			break
		case "unflagged":
			message = i18n.t("components:photoContextMenu.toastRemoveFlag")
			break
	}

	void saveCurrentAppState(message)
	showToast(message)
}

export const updateXmpForSelectedPhotos = async () => {
	const selectedPhotos = store.get(selectedPhotosAtom)
	const project = store.get(projectAtom)
	const metadataMap = store.get(metadataAtom)

	if (!selectedPhotos || selectedPhotos.length == 0) {
		return
	}

	await Promise.all(
		selectedPhotos.map(async (photo) => {
			const metadata = metadataMap[photo.id]
			await updatePhotoXmp(photo, metadata, project?.handle)
		}),
	)
}

export const readXmpForSelectedPhotos = async () => {
	const selectedPhotos = store.get(selectedPhotosAtom)
	const project = store.get(projectAtom)

	if (!selectedPhotos || selectedPhotos.length == 0) {
		return
	}

	await Promise.all(
		selectedPhotos.map(async (photo) => {
			await readPhotoXmp(photo, project?.handle)
		}),
	)
}

const updatePhotoXmp = async (
	photo: PhotoRecord,
	metadata?: PhotoMetadataRecord,
	handle?: FileSystemDirectoryHandle,
) => {
	const folderHandle = handle ?? store.get(projectAtom)?.handle
	const path = store.get(filePathsAtom)?.[photo.id]
	if (photo && folderHandle && path) {
		await updateXmpFile(
			{
				directoryHandle: folderHandle,
				filename: photo.fileName,
				path,
				create: true,
			},
			photo,
			metadata,
		)
	}
}

const readPhotoXmp = async (photo: PhotoRecord, handle?: FileSystemDirectoryHandle) => {
	const folderHandle = handle ?? store.get(projectAtom)?.handle
	const path = store.get(filePathsAtom)?.[photo.id]
	if (photo && folderHandle && path) {
		const xmpMetadata = await getXmpMetadata({
			directoryHandle: folderHandle,
			filename: photo.fileName,
			path,
		})
		if (xmpMetadata) updatePhoto(photo.id, xmpMetadata)
	}
}
